import { useCallback } from 'react';
import { useAuth } from '../auth/AuthContext';

/**
 * Hook para renovar o token de acesso antes da expiração.
 * 
 * Responsabilidades:
 * - Chamar o endpoint POST /login/refresh do identity-service
 * - Atualizar o token no AuthContext
 * - Resetar o timer de expiração
 * - Tratar erros de renovação (401, timeout, etc.)
 * 
 * @returns {Object} { refreshToken, isRefreshing, error }
 *   - refreshToken: função assíncrona que executa a renovação
 *   - isRefreshing: boolean indicando se está em progresso
 *   - error: erro da última tentativa de refresh (null se sucesso)
 */
export const useTokenRefresh = () => {
  const { updateToken, logout, getAuthToken } = useAuth();

  const refreshToken = useCallback(async () => {
    try {
      const currentToken = getAuthToken();

      if (!currentToken) {
        throw new Error('Token não disponível para renovação');
      }

      // Chamar endpoint de refresh do identity-service
      const response = await fetch(`${process.env.REACT_APP_AUTH_BASE_URL}/login/refresh`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${currentToken}`,
        },
        body: JSON.stringify({
          accessToken: currentToken,
        }),
      });

      if (!response.ok) {
        if (response.status === 401) {
          // Token expirou ou é inválido — fazer logout
          logout();
          throw new Error('Token expirado. Faça login novamente.');
        }
        throw new Error(`Erro ao renovar token: ${response.status}`);
      }

      const data = await response.json();

      if (!data.accessToken || !data.expiresIn) {
        throw new Error('Resposta inválida do servidor de autenticação');
      }

      // Atualizar token no AuthContext
      updateToken({
        accessToken: data.accessToken,
        expiresIn: data.expiresIn,
        tokenVersion: data.tokenVersion || undefined,
      });

      return {
        success: true,
        accessToken: data.accessToken,
        expiresIn: data.expiresIn,
      };
    } catch (err) {
      console.error('Erro ao renovar token:', err.message);
      return {
        success: false,
        error: err.message,
      };
    }
  }, [updateToken, logout, getAuthToken]);

  return { refreshToken };
};